import React, { useState, useEffect } from 'react';
import { useOrderStatus } from '../contexts/OrderStatusContext';
import { useNavigate } from 'react-router-dom';
import { Clock, ChefHat, CheckCircle, Truck, Sparkles, Zap } from 'lucide-react';

const OrderStatusBar = () => {
  console.log('🔄 OrderStatusBar: Component rendering');
  console.log('🌐 OrderStatusBar Environment:', process.env.NODE_ENV);
  console.log('🌐 OrderStatusBar Host:', window.location.host);
  
  const { currentOrder, orderStatus, getEstimatedDeliveryTime } = useOrderStatus();
  const navigate = useNavigate();
  const [animationKey, setAnimationKey] = useState(0);
  const [pulseAnimation, setPulseAnimation] = useState(false);

  // Animation effects - moved to top to avoid conditional hook calls
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationKey(prev => prev + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setPulseAnimation(prev => !prev);
    }, 1500);
    return () => clearInterval(pulseInterval);
  }, []);

  console.log('🔍 OrderStatusBar: Render check', {
    currentOrder: !!currentOrder,
    orderStatus: !!orderStatus,
    orderId: currentOrder?.orderId,
    status: orderStatus?.id,
    environment: process.env.NODE_ENV,
    host: window.location.host
  });

  if (!currentOrder || !orderStatus) {
    console.log('🔍 OrderStatusBar: Not rendering - missing data', {
      currentOrder: currentOrder,
      orderStatus: orderStatus,
      environment: process.env.NODE_ENV,
      host: window.location.host
    });
    return null;
  }

  console.log('🔍 OrderStatusBar: Rendering with data', {
    orderId: currentOrder.orderId,
    status: orderStatus.id,
    message: orderStatus.message
  });

  const estimatedTime = getEstimatedDeliveryTime();

  const getStatusIcon = () => {
    const iconStyle = {
      animation: `spin 1.5s linear infinite, pulse 1.2s ease-in-out infinite, bounce 2s ease-in-out infinite`,
      transform: pulseAnimation ? 'scale(1.15)' : 'scale(1)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
    };

    switch (orderStatus.id) {
      case 'placed':
        return (
          <div style={iconStyle}>
            <Clock size={18} color="white" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
            <Sparkles 
              size={8} 
              color="#FFD700" 
              style={{
                position: 'absolute',
                top: '-3px',
                right: '-3px',
                animation: 'sparkle 0.8s ease-in-out infinite, glow 1.5s ease-in-out infinite',
                filter: 'drop-shadow(0 1px 2px rgba(255,215,0,0.6))'
              }} 
            />
          </div>
        );
      case 'accepted':
      case 'preparing':
        return (
          <div style={iconStyle}>
            <ChefHat size={18} color="white" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
            <Zap 
              size={6} 
              color="#FF6B35" 
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                animation: 'zap 0.6s ease-in-out infinite, glow 1.2s ease-in-out infinite',
                filter: 'drop-shadow(0 1px 2px rgba(255,107,53,0.6))'
              }} 
            />
          </div>
        );
      case 'ready':
        return (
          <div style={iconStyle}>
            <CheckCircle size={18} color="white" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
            <Sparkles 
              size={8} 
              color="#00FF88" 
              style={{
                position: 'absolute',
                top: '-3px',
                right: '-3px',
                animation: 'sparkle 1s ease-in-out infinite, glow 1.8s ease-in-out infinite',
                filter: 'drop-shadow(0 1px 2px rgba(0,255,136,0.6))'
              }} 
            />
          </div>
        );
      case 'out_for_delivery':
        return (
          <div style={iconStyle}>
            <Truck size={18} color="white" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
            <Zap 
              size={6} 
              color="#A78BFA" 
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                animation: 'zap 0.8s ease-in-out infinite, glow 1.4s ease-in-out infinite',
                filter: 'drop-shadow(0 1px 2px rgba(167,139,250,0.6))'
              }} 
            />
          </div>
        );
      default:
        return (
          <div style={iconStyle}>
            <Clock size={18} color="white" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
          </div>
        );
    }
  };

  const handleStatusClick = () => {
    // Navigate to order details or tracking page
    navigate('/order-history');
  };

  return (
    <>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
          
          @keyframes sparkle {
            0%, 100% { 
              opacity: 0; 
              transform: scale(0.3) rotate(0deg); 
            }
            25% { 
              opacity: 1; 
              transform: scale(1.4) rotate(90deg); 
            }
            50% { 
              opacity: 0.8; 
              transform: scale(1.1) rotate(180deg); 
            }
            75% { 
              opacity: 1; 
              transform: scale(1.3) rotate(270deg); 
            }
          }
          
          @keyframes zap {
            0%, 100% { 
              opacity: 0.2; 
              transform: scale(0.6) rotate(0deg); 
            }
            20% { 
              opacity: 1; 
              transform: scale(1.5) rotate(72deg); 
            }
            40% { 
              opacity: 0.7; 
              transform: scale(1.2) rotate(144deg); 
            }
            60% { 
              opacity: 1; 
              transform: scale(1.4) rotate(216deg); 
            }
            80% { 
              opacity: 0.8; 
              transform: scale(1.1) rotate(288deg); 
            }
          }
          
          @keyframes slideIn {
            0% { 
              transform: translateY(100%) scale(0.8); 
              opacity: 0; 
            }
            50% {
              transform: translateY(-5px) scale(1.02);
              opacity: 0.8;
            }
            100% { 
              transform: translateY(0) scale(1); 
              opacity: 1; 
            }
          }
          
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            25% { background-position: 100% 0%; }
            50% { background-position: 100% 100%; }
            75% { background-position: 0% 100%; }
            100% { background-position: 0% 50%; }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-2px) rotate(1deg); }
            50% { transform: translateY(-4px) rotate(0deg); }
            75% { transform: translateY(-2px) rotate(-1deg); }
          }
          
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-8px); }
            60% { transform: translateY(-4px); }
          }
          
          @keyframes wiggle {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(3deg); }
            75% { transform: rotate(-3deg); }
          }
          
          @keyframes glow {
            0%, 100% { 
              box-shadow: 0 0 5px rgba(255, 255, 255, 0.3), 0 0 10px rgba(255, 255, 255, 0.2);
            }
            50% { 
              box-shadow: 0 0 20px rgba(255, 255, 255, 0.6), 0 0 30px rgba(255, 255, 255, 0.4), 0 0 40px rgba(255, 255, 255, 0.2);
            }
          }
          
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          
          @keyframes rainbow {
            0% { filter: hue-rotate(0deg); }
            100% { filter: hue-rotate(360deg); }
          }
        `}
      </style>
      
      <div 
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: `linear-gradient(135deg, 
            ${orderStatus.id === 'placed' ? '#3B82F6, #1E40AF, #60A5FA' : 
              orderStatus.id === 'accepted' || orderStatus.id === 'preparing' ? '#F59E0B, #D97706, #FBBF24' :
              orderStatus.id === 'ready' ? '#10B981, #059669, #34D399' :
              orderStatus.id === 'out_for_delivery' ? '#8B5CF6, #7C3AED, #A78BFA' : '#3B82F6, #1E40AF, #60A5FA'})`,
          backgroundSize: '300% 300%',
          animation: 'gradientShift 2s ease infinite, slideIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55), glow 3s ease-in-out infinite',
          borderTop: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.2), 0 0 30px rgba(255, 255, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          zIndex: 9999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          overflow: 'hidden',
          minHeight: '80px'
        }}
      >
        {/* Shimmer overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s ease-in-out infinite',
            pointerEvents: 'none'
          }}
        />
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px', // Reduced by 40%
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '48px' // Reduced from ~80px
          }}
          onClick={handleStatusClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.03) translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 -8px 25px rgba(0, 0, 0, 0.25), 0 0 40px rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.animation = 'bounce 0.6s ease-in-out, wiggle 0.8s ease-in-out';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1) translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.animation = '';
          }}
        >
          {/* Animated background overlay */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%, rgba(255,255,255,0.15) 100%)',
              animation: `gradientShift 1.5s ease infinite ${animationKey * 0.3}s, rainbow 4s linear infinite`,
              pointerEvents: 'none'
            }}
          />
          {/* Left side - Status info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px', // Reduced by 40%
            flex: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px', // Reduced by 40%
              height: '36px', // Reduced by 40%
              background: 'rgba(255, 255, 255, 0.25)',
              borderRadius: '50%',
              border: '1.5px solid rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              position: 'relative',
              animation: 'float 2.5s ease-in-out infinite, glow 2s ease-in-out infinite',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              overflow: 'hidden'
            }}>
              {getStatusIcon()}
              {/* Icon glow effect */}
              <div 
                style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  right: '-2px',
                  bottom: '-2px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  pointerEvents: 'none'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px' // Reduced by 40%
              }}>
                <h3 style={{
                  fontSize: '14px', // Reduced by 40%
                  fontWeight: '700',
                  color: 'white',
                  margin: 0,
                  textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(255, 255, 255, 0.2)',
                  letterSpacing: '0.3px',
                  animation: 'pulse 2s ease-in-out infinite'
                }}>
                  B.Tech Bawarchi
                </h3>
                <div style={{
                  width: '4px', // Reduced by 40%
                  height: '4px', // Reduced by 40%
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  animation: 'bounce 1.2s ease-in-out infinite, glow 1.5s ease-in-out infinite'
                }} />
                <span style={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '12px', // Reduced by 40%
                  fontWeight: '600',
                  animation: 'wiggle 2s ease-in-out infinite'
                }}>
                  ▸
                </span>
              </div>
              <p style={{
                fontSize: '11px', // Reduced by 40%
                color: 'rgba(255, 255, 255, 0.95)',
                margin: '4px 0 0 0', // Reduced by 40%
                fontWeight: '500',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 0 8px rgba(255, 255, 255, 0.1)',
                lineHeight: '1.3',
                animation: 'pulse 2.5s ease-in-out infinite'
              }}>
                {orderStatus.message}
              </p>
            </div>
        </div>

          {/* Right side - Estimated time */}
          {estimatedTime && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.25)',
              color: 'white',
              padding: '6px 12px', // Reduced by 40%
              borderRadius: '16px', // Reduced by 40%
              fontSize: '10px', // Reduced by 40%
              fontWeight: '600',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.4), 0 0 8px rgba(255, 255, 255, 0.2)',
              boxShadow: '0 3px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              animation: 'bounce 1.8s ease-in-out infinite, glow 2.2s ease-in-out infinite, wiggle 3s ease-in-out infinite',
              position: 'relative',
              zIndex: 1,
              overflow: 'hidden'
            }}>
              {estimatedTime}
              {/* Shimmer effect on badge */}
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 2.5s ease-in-out infinite',
                  pointerEvents: 'none'
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderStatusBar;
